import { Navigate, Route, Routes } from 'react-router-dom';
import { BdeseExercicesPage } from '@/features/bdese/pages/BdeseExercicesPage';
import { BdeseConsultationPage } from '@/features/bdese/pages/BdeseConsultationPage';
import { BdeseSaisiePage } from '@/features/bdese/pages/BdeseSaisiePage';
import { useEtatDuClient } from '@/hooks/useEstClientSeul';

import { ROUTES } from '@/constants/app';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { authRoutes } from '@/features/auth/routes';
import { Force2FASetupPage } from '@/features/auth/pages/Force2FASetupPage';
import { AssistantPage } from '@/features/assistant';
import { AideAssistantPage } from '@/features/assistant/pages/AideAssistantPage';
import { AutomationsPage } from '@/features/automation';
import { DomainDetailPage } from '@/features/domains/pages/DomainDetailPage';
import { DomainsPage } from '@/features/domains/pages/DomainsPage';
import { FolderDetailPage } from '@/features/domains/pages/FolderDetailPage';
import { GlobalFoldersPage } from '@/features/domains/pages/GlobalFoldersPage';
import { EmployeeDetailPage } from '@/features/employees/pages';
import { DocumentsPage } from '@/features/ged/pages';
// C3 — SIRH : ajout de trois écrans, sans refonte de ceux qui existent.
import {
  LeaveApprovalsPage,
  MyLeavePage,
  VariableCampaignPage,
} from '@/features/sirh';
import { VaultDetailPage } from '@/features/vault/pages/VaultDetailPage';
import { InvitationAcceptPage } from '@/features/vault/pages/InvitationAcceptPage';
import {
  VaultInitializePage,
  VaultUserDashboard,
  VaultCompliancePage,
  VaultComplianceDashboardPage,
  VaultHelpDashboardPage,
  VaultHelpPage,
} from '@/features/vault/pages';
import { RegisterFromInvitationPage } from '@/features/auth/pages/RegisterFromInvitationPage';
import { EmployeeVaultPage } from '@/features/vault/pages/EmployeeVaultPage';
import { DashboardPage } from '@/features/dashboard-rh/pages/DashboardPage';
import { PublicDashboardPage } from '@/features/dashboard-rh/pages/PublicDashboardPage';
import { legalRoutes } from '@/features/legal/routes';
import { PageSaisieClient } from '@/features/demande-variables/pages/PageSaisieClient';
import { PageSaisiePourLeCabinet } from '@/features/demande-variables/pages/PageSaisiePourLeCabinet';
import { NotificationsPage } from '@/features/notifications';
import { InvoicesPage } from '@/features/payments';
import { SendersPage } from '@/features/senders/pages/SendersPage';
import { BillingPage } from '@/features/settings/pages/BillingPage';
import { ProfilePage } from '@/features/settings/pages/ProfilePage';
import { SettingsPage } from '@/features/settings/pages/SettingsPage';
import { SupportPage } from '@/features/support';
import { ChangelogPage } from '@/features/changelog';
import { TemplatesPage } from '@/features/templates';
import { OrganisationSettingsPage } from '@/features/organisations/pages/OrganisationSettingsPage';
import { AcceptOrgInvitationPage } from '@/features/organisations/pages/AcceptOrgInvitationPage';
import { ProtectedLayout } from './ProtectedLayout';
import { Dashboard } from '@/features/dashboard/pages/Dashboard';
import { AdminUsersPage } from '@/features/admin/pages/AdminUsersPage';
import { AccepterInvitationPage } from '@/features/invitations/pages/AccepterInvitationPage';
import { MonEntreprisePage } from '@/features/invitations/pages/MonEntreprisePage';
import { InviterPage } from '@/features/invitations/pages/InviterPage';
import { destinationClientParDefaut } from './navigation-client';

/**
 * Où l'on arrive quand aucune route ne correspond — et après la connexion.
 *
 * Tant qu'on ne sait pas encore à qui on a affaire, on n'envoie NULLE PART :
 * rediriger puis rediriger encore ferait clignoter l'écran et pourrait piéger
 * le bouton « précédent » du navigateur.
 */
function AtterrissageParDefaut() {
  const etatClient = useEtatDuClient();
  const { estClientSeul, erreurChargement } = etatClient;
  if (erreurChargement) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-xl border border-neutral-200 bg-white p-6 text-center shadow-sm">
          <h1 className="text-base font-semibold text-neutral-900">
            Accès momentanément indisponible
          </h1>
          <p className="mt-2 text-sm leading-6 text-neutral-600">
            Nous n&apos;avons pas pu vérifier votre espace. Réessayez avant de
            poursuivre.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 min-h-11 rounded-lg px-4 text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--marque-accent)' }}
          >
            Réessayer
          </button>
        </div>
      </main>
    );
  }
  if (estClientSeul === null) return null;
  return (
    <Navigate
      to={
        estClientSeul ? destinationClientParDefaut(etatClient) : ROUTES.FOLDERS
      }
      replace
    />
  );
}

export function AppRouter() {
  return (
    <Routes>
      {/* Routes d'authentification */}
      {authRoutes}

      {/* Routes légales - Publiques */}
      {legalRoutes}

      {/* Route publique pour le tableau de bord */}
      <Route
        path="/public/dashboard/:dashboardUuid"
        element={<PublicDashboardPage />}
      />

      {/* Le client transmet ses éléments de paie depuis son lien, sans compte
          (demande de variables). */}
      <Route path="/saisie/:secret" element={<PageSaisieClient />} />

      {/* Route publique pour accepter une invitation vault */}
      <Route
        path="/vault/invitation/:token"
        element={<InvitationAcceptPage />}
      />

      {/* [C2] L'espace de l'entreprise cliente — le seul du produit qui ne
          soit pas celui du cabinet. */}
      <Route
        path="/mon-entreprise"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <MonEntreprisePage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* [C2 · règle 14] Inviter est un ÉCRAN PLEIN qui remplace « Mon
          entreprise », pas un formulaire qui s'ajoute dedans. */}
      <Route
        path="/mon-entreprise/inviter"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <InviterPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* Route pour accepter une invitation d'ACTEUR EMPLOYEUR (C2).
          Publique : l'invitée n'a pas encore de raison d'être connectée, et
          c'est le jeton qui fait foi. */}
      <Route path="/invitation/:jeton" element={<AccepterInvitationPage />} />

      {/* Route pour accepter une invitation organisation */}
      <Route
        path="/organisations/invitation/:token"
        element={<AcceptOrgInvitationPage />}
      />

      {/* Route publique pour l'inscription via invitation vault */}
      <Route
        path="/register/invitation/:token"
        element={<RegisterFromInvitationPage />}
      />

      {/* Route pour le setup 2FA obligatoire (vault users) */}
      <Route
        path="/setup-2fa"
        element={
          <ProtectedRoute skipForce2FA allowVaultUser>
            <Force2FASetupPage />
          </ProtectedRoute>
        }
      />

      {/* Route pour l'initialisation du vault (vault users) */}
      <Route
        path="/vault/initialize/:vaultId"
        element={
          <ProtectedRoute skipForce2FA allowVaultUser>
            <VaultInitializePage />
          </ProtectedRoute>
        }
      />

      {/* Route protégée pour les employés (interface simplifiée) */}
      <Route
        path="/my-vault"
        element={
          <ProtectedRoute allowVaultUser>
            <EmployeeVaultPage />
          </ProtectedRoute>
        }
      />

      {/* Dashboard simplifié pour les vault users */}
      <Route
        path="/my-vaults"
        element={
          <ProtectedRoute allowVaultUser>
            <VaultUserDashboard />
          </ProtectedRoute>
        }
      />

      {/* Page de conformité pour les vault users */}
      <Route
        path="/my-vaults/compliance"
        element={
          <ProtectedRoute allowVaultUser>
            <VaultCompliancePage />
          </ProtectedRoute>
        }
      />

      {/* Page d'aide / FAQ pour les vault users */}
      <Route
        path="/my-vaults/help"
        element={
          <ProtectedRoute allowVaultUser>
            <VaultHelpPage />
          </ProtectedRoute>
        }
      />

      <Route
        path={`${ROUTES.VAULT}/:vaultId`}
        element={
          <ProtectedRoute allowVaultUser>
            <VaultDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Anciennes routes de paiement supprimées (abonnement annuel retiré) */}

      {/* Routes protégées avec layout (abonnement non requis) */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <Dashboard />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.DOMAINS}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <DomainsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* L'assistant en langage naturel — décision du 10/09/2026. */}
      <Route
        path={ROUTES.ASSISTANT}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <AssistantPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />
      {/* Sa documentation, dans l'application (décision du 12/09/2026). */}
      <Route
        path={ROUTES.AIDE.ASSISTANT}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <AideAssistantPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/domains/:domainUuid"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <DomainDetailPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.FOLDERS}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <GlobalFoldersPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/folders/:folderUuid"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <FolderDetailPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* La saisie du mois d'un dossier, faite par le cabinet (depuis la
          collecte ou l'onglet Variables). */}
      <Route
        path="/collecte/:folderUuid/saisie"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <PageSaisiePourLeCabinet />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/folders/:folderUuid/employees/:employeeUuid"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <EmployeeDetailPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/folders/:folderUuid/dashboard/:dashboardUuid"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <DashboardPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.AUTOMATION}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <AutomationsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.DOCUMENTS}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <DocumentsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* C4 — BDESE : les exercices d'un dossier, puis la consultation. */}
      <Route
        path={ROUTES.BDESE.EXERCICES}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <BdeseExercicesPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.BDESE.SAISIE}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <BdeseSaisiePage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.BDESE.EXERCICE}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <BdeseConsultationPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* C3 — SIRH : absences, approbations, campagne de variables. */}
      <Route
        path={ROUTES.SIRH.MY_LEAVE}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <MyLeavePage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.SIRH.APPROVALS}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <LeaveApprovalsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.SIRH.VARIABLES}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <VariableCampaignPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vault-compliance"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <VaultComplianceDashboardPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/vault-help"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <VaultHelpDashboardPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* Routes Vault - Réservées aux vault_user (employés) uniquement
          Les gestionnaires n'ont plus accès direct aux coffres-forts
          Ils peuvent voir l'historique via le drawer dans la page du dossier */}
      {/* <Route
        path={ROUTES.VAULT}
        element={
          <ProtectedRoute allowVaultUser vaultUserOnly>
            <ProtectedLayout>
              <VaultsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      /> */}

      {/* <Route
        path="/vaults/generation"
        element={
          <ProtectedRoute vaultUserOnly>
            <ProtectedLayout>
              <VaultGenerationPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      /> */}

      <Route
        path={ROUTES.ORGANISATIONS}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <OrganisationSettingsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/senders"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <SendersPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.INVOICES}
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <InvoicesPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <NotificationsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <TemplatesPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path={ROUTES.CHANGELOG}
        element={
          <ProtectedRoute allowVaultUser>
            <ProtectedLayout>
              <ChangelogPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/support"
        element={
          <ProtectedRoute allowVaultUser>
            <ProtectedLayout>
              <SupportPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute allowVaultUser>
            <ProtectedLayout>
              <SettingsPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <ProfilePage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/billing"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <BillingPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* Administration (réservé au rôle admin — l'API refuse les autres) */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <ProtectedLayout>
              <AdminUsersPage />
            </ProtectedLayout>
          </ProtectedRoute>
        }
      />

      {/* Redirection par défaut — pas la même selon qui se connecte.
          Une entreprise cliente atterrissait dans « Tous les dossiers », l'écran
          de travail du cabinet. Elle arrive maintenant chez elle. */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <AtterrissageParDefaut />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
